import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { msalInstance, apiScopes } from '@/features/auth/lib/msalConfig'
import { InteractionRequiredAuthError, type AccountInfo } from '@azure/msal-browser'
import { ME_KEY, queryClient } from '@/shared/api/queryClient'
import { ApiError } from '@/shared/api/ApiError'
import { setDatabaseWaking } from '@/shared/api/databaseWaking'
import type { ApiErrorBody, ApiResponse } from '@/shared/types/api'

/** Axios instance for billing-api. Auth is an MSAL bearer token on every request. */
// No default Content-Type: a forced JSON header makes Axios serialise FormData uploads as JSON.
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
})

/**
 * In-flight token acquisition, shared across concurrent requests.
 * Without this, N parallel requests on an expired token each trigger their own
 * interactive prompt.
 */
let tokenRequest: Promise<string> | null = null

async function acquireToken(account: AccountInfo): Promise<string> {
  if (tokenRequest) return tokenRequest

  tokenRequest = (async () => {
    try {
      const result = await msalInstance.acquireTokenSilent({ scopes: apiScopes, account })
      return result.accessToken
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        // Redirect, not popup: an interceptor runs outside a user gesture, so
        // browsers block popups here and the request would fail or hang.
        await msalInstance.acquireTokenRedirect({ scopes: apiScopes, account })
        // Navigation is underway — never resolve, so nothing is sent unauthenticated.
        return new Promise<string>(() => {})
      }
      throw err
    } finally {
      tokenRequest = null
    }
  })()

  return tokenRequest
}

/** Attach a fresh access token to every outgoing request. */
apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const accounts = msalInstance.getAllAccounts()
  if (accounts.length === 0) {
    // Not signed in — let the request through; the 401 will trigger the auth flow
    return config
  }

  const account = msalInstance.getActiveAccount() ?? accounts[0]
  const token = await acquireToken(account)
  config.headers.Authorization = `Bearer ${token}`

  return config
})

export { ApiError }

/** Map every failure to `ApiError` and keep `/api/me` and the waking banner in sync. */
apiClient.interceptors.response.use(
  (res) => {
    setDatabaseWaking(false)
    return res
  },
  async (err: unknown) => {
    if (!axios.isAxiosError(err)) return Promise.reject(err)

    const res = (err as AxiosError<Partial<ApiErrorBody> | Blob>).response
    if (!res) return Promise.reject(new ApiError(0, 'NETWORK', 'Network error. Check your connection.'))

    // With responseType 'blob' (file downloads) the JSON error body arrives as a Blob.
    let data = res.data
    if (data instanceof Blob) {
      try { data = JSON.parse(await data.text()) as Partial<ApiErrorBody> } catch { data = {} }
    }
    const body = (data as Partial<ApiErrorBody> | undefined)?.error
    const code = body?.code ?? 'UNKNOWN'
    setDatabaseWaking(code === 'DATABASE_WAKING')

    // A rejection elsewhere means the cached /api/me is stale. Skip /me itself, or a 403 would refetch forever.
    const isMeCall = err.config?.url === '/me'
    if ((res.status === 401 || res.status === 403) && !isMeCall) {
      queryClient.invalidateQueries({ queryKey: ME_KEY })
    }

    return Promise.reject(
      new ApiError(
        res.status,
        code,
        body?.message ?? 'Something went wrong.',
        res.headers['x-correlation-id'] as string | undefined,
        body?.details,
        Number(res.headers['retry-after']) || undefined,
      ),
    )
  },
)

/** Typed wrappers that unwrap the `{ data }` envelope. */
export const api = {
  get:  <T>(url: string, params?: object) => apiClient.get<ApiResponse<T>>(url, { params }).then((r) => r.data.data),
  post: <T>(url: string, body?: unknown)  => apiClient.post<ApiResponse<T>>(url, body).then((r) => r.data.data),
  put:  <T>(url: string, body?: unknown)  => apiClient.put<ApiResponse<T>>(url, body).then((r) => r.data.data),
}
